import Phaser from 'phaser';
import { ProtoCell } from '../objects/ProtoCell';
import { GameUISystem } from './GameUISystem';

export class ProtoCellUISystem {
    private scene: Phaser.Scene;
    private protoCell: ProtoCell;
    private gameUI: GameUISystem;
    private buttons: { [key: string]: Phaser.GameObjects.Container } = {};
    private isTransitionComplete: boolean = false;
    
    // Track assembly messages
    private messages = {
        start: "Assemble lipids along the boundary to create a membrane. The membrane will stabilize when enough lipids are properly positioned.",
        lipidStart: "Good start! Continue adding lipids to form a complete membrane.",
        lipidProgress: "Your membrane is taking shape! Now try adding some molecules inside the cell.",
        lipidComplete: "Membrane complete! Add essential molecules inside to make a functional protocell.",
        moleculeStart: "Great! You've added molecules inside. These can engage in chemical reactions.",
        moleculeProgress: "Your protocell is gaining complexity! Keep adding more molecules.",
        moleculeComplete: "Amazing! Your protocell now has all the essential components.",
        complete: "Congratulations! You have successfully assembled a stable protocell that could survive in the primordial ocean. This primitive cell can contain simple molecules and potentially support early chemical reactions - a critical step toward life on Earth."
    };
    
    constructor(scene: Phaser.Scene, protoCell: ProtoCell) {
        this.scene = scene;
        this.protoCell = protoCell;
        
        // Create the GameUISystem
        this.gameUI = new GameUISystem(scene, {
            progressBarColor: 0x4CAF50,
            buttonBgColor: 0x4CAF50
        });
        
        // Create UI elements
        this.createUI();
        
        // Listen for progress updates from protocell
        this.protoCell.progressEmitter.on('progress', this.updateProgress, this);
        
        // Add resize listener
        this.scene.scale.on('resize', this.handleResize, this);
    }
    
    private createUI(): void {
        // Create progress bar (top left)
        this.gameUI.createProgressBar('assembly', 20, 20, "Assembly Progress: 0%", 0);
        
        // Create control buttons (top right)
        const screenWidth = this.scene.scale.width;
        
        // Auto-assemble button
        this.buttons.autoAssemble = this.gameUI.createButton(
            'autoAssemble',
            screenWidth - 80,
            20,
            "Auto-Assemble",
            this.handleAutoAssemble,
            this,
            200
        );
        
        // Reset button
        this.buttons.reset = this.gameUI.createButton(
            'reset',
            screenWidth - 80,
            60,
            "Reset",
            this.handleReset,
            this,
            200
        );
        
        // Create info panel at bottom
        this.gameUI.createInfoPanel(this.messages.start);
        
        // Success message panel (initially not shown)
        const successPanel = this.gameUI.createSuccessPanel(
            "Congratulations!",
            this.messages.complete,
            "Continue to Next Stage",
            this.handleContinue,
            this
        );
        
        // The success panel is created but not shown yet
        successPanel.setAlpha(0);
    }
    
    private updateProgress(progress: number): void {
        if (this.isTransitionComplete) return;

        // Update progress text and bar
        this.gameUI.updateProgressBar('assembly', progress, `Assembly Progress: ${progress}%`);
        
        // If we reach 100%, trigger the transition
        if (progress >= 100) {
            this.handleCompletionTransition();
        } else {
            // Update info text based on progress milestones
            this.updateInfoText();
        }
    }
    
    private updateInfoText(): void {
        const lipidCount = (this.protoCell as any).lipidsInPosition || 0;
        const moleculeCount = (this.protoCell as any).moleculesInPosition || 0;
        const neededLipids = (this.protoCell as any).neededLipids || 20;
        const neededMolecules = (this.protoCell as any).neededMolecules || 8;
        
        if (lipidCount === 1) {
            this.gameUI.updateInfoPanel(this.messages.lipidStart);
        } else if (lipidCount >= neededLipids / 2 && lipidCount < neededLipids) {
            this.gameUI.updateInfoPanel(this.messages.lipidProgress);
        } else if (lipidCount >= neededLipids && moleculeCount === 0) {
            this.gameUI.updateInfoPanel(this.messages.lipidComplete);
        } else if (moleculeCount === 1) {
            this.gameUI.updateInfoPanel(this.messages.moleculeStart);
        } else if (moleculeCount >= neededMolecules / 2 && moleculeCount < neededMolecules) {
            this.gameUI.updateInfoPanel(this.messages.moleculeProgress);
        } else if (moleculeCount >= neededMolecules) {
            this.gameUI.updateInfoPanel(this.messages.moleculeComplete);
        }
    }
    
    public showSuccessMessage(): void {
        // Show success message using the GameUISystem
        this.gameUI.showSuccessPanel();
    }
    
    private handleAutoAssemble(): void {
        console.log('Auto-assemble button clicked');
        
        // Toggle auto-assemble mode
        const buttons = this.gameUI.getContainer().list.filter(obj => 
            obj instanceof Phaser.GameObjects.Container &&
            obj.getData('id') === 'autoAssemble'
        );
        
        if (buttons.length > 0) {
            const button = buttons[0] as Phaser.GameObjects.Container;
            const isActive = !button.getData('active');
            button.setData('active', isActive);
            
            // Update button text
            const buttonObjects = button.list.filter(obj => obj instanceof Phaser.GameObjects.Text);
            if (buttonObjects.length > 0) {
                const buttonText = buttonObjects[0] as Phaser.GameObjects.Text;
                buttonText.setText(isActive ? "Stop Auto-Assemble" : "Auto-Assemble");
            }
            
            // Emit auto-assemble event for the scene to handle
            this.scene.events.emit('autoAssemble', isActive);
        }
    }
    
    private handleReset(): void {
        console.log('Reset button clicked');
        
        // Turn off auto-assemble if it's on
        const buttons = this.gameUI.getContainer().list.filter(obj => 
            obj instanceof Phaser.GameObjects.Container &&
            obj.getData('id') === 'autoAssemble'
        );
        
        if (buttons.length > 0) {
            const button = buttons[0] as Phaser.GameObjects.Container;
            if (button.getData('active')) {
                button.setData('active', false);
                
                // Update button text
                const buttonObjects = button.list.filter(obj => obj instanceof Phaser.GameObjects.Text);
                if (buttonObjects.length > 0) {
                    const buttonText = buttonObjects[0] as Phaser.GameObjects.Text;
                    buttonText.setText("Auto-Assemble");
                }
                
                // Notify scene that auto-assemble is off
                this.scene.events.emit('autoAssemble', false);
            }
        }
        
        // Reset the protocell
        this.protoCell.reset();
        
        // Reset progress bar
        this.gameUI.updateProgressBar('assembly', 0, "Assembly Progress: 0%");
        
        // Reset info text
        this.gameUI.updateInfoPanel(this.messages.start);
        
        // Emit reset event for the scene to handle
        this.scene.events.emit('reset');
    }
    
    private handleContinue(): void {
        console.log('Continue button clicked');
        
        // Calculate membrane stability based on assembly quality
        const membraneStability = (this.protoCell as any).lipidsInPosition / (this.protoCell as any).neededLipids * 100;
        
        // Get reference to assembly bar
        const assemblyBar = this.gameUI.getProgressBar('assembly');
        
        if (!assemblyBar) {
            console.error('Could not find assembly progress bar');
            return;
        }
        
        // Find and hide the success panel
        const successPanel = this.gameUI.getContainer().list.find(obj => 
            obj instanceof Phaser.GameObjects.Container && 
            obj === this.gameUI['successPanel']
        ) as Phaser.GameObjects.Container;
        
        if (successPanel) {
            this.scene.tweens.add({
                targets: successPanel,
                alpha: 0,
                duration: 300,
                onComplete: () => {
                    // Create membrane stability bar at the top left (same position as assembly bar)
                    const stabilityBar = this.gameUI.createProgressBar(
                        'membraneStability',
                        assemblyBar.container.x,
                        assemblyBar.container.y,
                        `Membrane Stability: ${Math.round(membraneStability)}%`,
                        membraneStability
                    );
                    
                    // Make assembly bar invisible
                    assemblyBar.container.setAlpha(0);
                    
                    // First make sure the info panel is visible again (it might have been hidden by showSuccessPanel)
                    const infoPanel = this.gameUI['infoPanel'];
                    if (infoPanel) {
                        infoPanel.setAlpha(1);
                    }
                    
                    // Now update info panel with stability information
                    this.gameUI.updateInfoPanel(
                        `Your protocell membrane has a stability of ${Math.round(membraneStability)}%. ` +
                        `With ${(this.protoCell as any).lipidsInPosition} lipids and ` +
                        `${(this.protoCell as any).moleculesInPosition} internal molecules, ` +
                        `your cell is now stable and ready for the next phase.`
                    );
                }
            });
        }
    }
    
    public handleResize(): void {
        const screenWidth = this.scene.scale.width;

        // Update button positions
        if (this.buttons.autoAssemble) {
            this.buttons.autoAssemble.setPosition(screenWidth - 80, 20);
        }
        if (this.buttons.reset) {
            this.buttons.reset.setPosition(screenWidth - 80, 60);
        }
    }
    
    public update(): void {
        // No additional update logic needed - handled by the GameUISystem
    }

    private handleCompletionTransition(): void {
        this.isTransitionComplete = true;
        console.log('Starting assembly completion transition');

        // Get reference to assembly bar
        const assemblyBar = this.gameUI.getProgressBar('assembly');

        if (!assemblyBar) {
            console.error('Could not find assembly progress bar for transition');
            return;
        }

        // First, create a pulse effect on just the assembly bar fill
        this.scene.tweens.add({
            targets: [assemblyBar.fill],
            scaleX: 1.05,
            scaleY: 1.1,
            alpha: 0.8,
            yoyo: true,
            duration: 150,
            repeat: 2,
            ease: 'Sine.easeInOut',
            onComplete: () => {
                console.log('Pulse complete, starting bar transition');
                // After pulse, show the success message and animate the transition
                this.gameUI.updateInfoPanel(this.messages.complete);
                
                // Create the stability bar now - first get assembly position
                const assemblyPos = { x: assemblyBar.container.x, y: assemblyBar.container.y };
                
                // Create the stability bar at a position below the assembly bar
                const stabilityBar = this.gameUI.createProgressBar(
                    'stability',
                    assemblyPos.x,
                    assemblyPos.y + 60, // Position below the assembly bar
                    "Membrane Stability: 0%",
                    0
                );
                
                // Update the stability bar with current progress
                this.gameUI.updateProgressBar(
                    'stability',
                    this.protoCell.progress,
                    `Membrane Stability: ${Math.round(this.protoCell.progress)}%`
                );
                
                // Make the stability bar initially invisible
                const stabBar = this.gameUI.getProgressBar('stability');
                if (stabBar && stabBar.container) {
                    stabBar.container.setAlpha(0);
                }
                
                // Begin the animation sequence
                this.animateProgressBarTransition(assemblyBar, stabilityBar);
            }
        });
    }

    private animateProgressBarTransition(assemblyBar: any, stabilityBar: any): void {
        // Store original positions
        const assemblyPos = { x: assemblyBar.container.x, y: assemblyBar.container.y };
        
        // Fade out assembly bar text with a slight upward movement
        if (assemblyBar.text) {
            this.scene.tweens.add({
                targets: assemblyBar.text,
                alpha: 0,
                y: assemblyBar.text.y - 10,
                duration: 500,
                ease: 'Power2'
            });
        }
        
        // Make stability bar text appear
        if (stabilityBar.text) {
            stabilityBar.text.setAlpha(0);
            this.scene.tweens.add({
                targets: stabilityBar.text,
                alpha: 1,
                duration: 500,
                delay: 300,
                ease: 'Power2'
            });
        }
        
        // Fade in the stability bar
        this.scene.tweens.add({
            targets: stabilityBar.container,
            alpha: 1,
            duration: 800,
            delay: 200,
            ease: 'Power2'
        });
        
        // Move stability bar to assembly bar's position with a bounce effect
        this.scene.tweens.add({
            targets: stabilityBar.container,
            x: assemblyPos.x,
            y: assemblyPos.y,
            duration: 800,
            delay: 600, // Delay the movement until after fade-in starts
            ease: 'Back.easeOut',
            onComplete: () => {
                console.log('Stability bar moved to assembly position');
                // After movement is complete, fade out and destroy assembly bar
                this.scene.tweens.add({
                    targets: [assemblyBar.container],
                    alpha: 0,
                    duration: 300,
                    onComplete: () => {
                        assemblyBar.container.destroy();
                        
                        // After a short delay, show the success panel
                        this.scene.time.delayedCall(500, () => {
                            this.showSuccessMessage();
                        });
                    }
                });
            }
        });
    }
} 